import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XCircle, Users, MessageSquare, TrendingDown, UtensilsCrossed } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

interface CancellationsAnalyticsProps {
  storeId: number;
  dateFrom: string;
  dateTo: string;
}

/** Μία ακύρωση στο τραπέζι (ημερολόγιο ακυρώσεων): είδος της «Διόρθωσης» ή ολόκληρο τραπέζι. */
export interface DineInCancellationRow {
  id: number;
  kind: 'item' | 'table';
  at: string | null;
  table: string | null;
  item: string;
  quantity: number | null;
  amount: number;
  reason: string | null;
  who: string;
}

interface CancellationData {
  summary: {
    total_cancelled: number;
    total_orders: number;
    cancellation_rate: number;
    revenue_lost: number;
  };
  by_reason: Array<{ reason: string; count: number; revenue_lost: number }>;
  by_employee: Array<{ employee_id: number | null; name: string; count: number; revenue_lost: number }>;
  timeline: Array<{ day: string; count: number; revenue_lost: number }>;
  /** Ακυρώσεις στα τραπέζια, στο ίδιο παράθυρο· null = δεν φόρτωσαν, λείπει = παλιός server. */
  dine_in?: {
    items: { count: number; amount: number };
    tables: { count: number; amount: number };
    rows: DineInCancellationRow[];
    truncated: boolean;
  } | null;
}

const formatEuro = (n: number) =>
  new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(n || 0);
const formatQty = (n: number) => new Intl.NumberFormat('el-GR', { maximumFractionDigits: 3 }).format(n);
// Ώρα Ελλάδας (ο server είναι UTC), 24ωρη ρητά.
const formatWhen = (iso: string | null) => (iso
  ? new Intl.DateTimeFormat('el-GR', { timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso))
  : '—');

const CancellationsAnalytics: React.FC<CancellationsAnalyticsProps> = ({ storeId, dateFrom, dateTo }) => {
  const { t } = useTranslation(['analytics', 'common']);
  const [data, setData] = useState<CancellationData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/orders/${storeId}/cancellations?from=${dateFrom}&to=${dateTo}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` } }
        );
        if (r.ok && !cancelled) setData(await r.json());
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [storeId, dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="bg-surface-1/50 border border-line rounded-2xl p-6 animate-pulse">
        <div className="h-4 w-40 bg-surface-2 rounded mb-4" />
        <div className="h-24 bg-surface-2 dark:bg-surface-2/50 rounded" />
      </div>
    );
  }

  const dineIn = data?.dine_in;
  const dineInTotal = dineIn ? dineIn.items.count + dineIn.tables.count : 0;
  const noOrders = !data || data.summary.total_cancelled === 0;

  // Ακυρώσεις στα τραπέζια: μόνο όταν υπάρχουν (κατάστημα χωρίς τραπέζια δεν βλέπει κενό πίνακα).
  const dineInBlock = dineIn && dineInTotal > 0 ? (
    <div className="bg-surface-1/50 border border-line rounded-2xl p-5" data-testid="dinein-cancellations">
      <div className="flex items-center gap-2 mb-1">
        <UtensilsCrossed className="w-5 h-5 text-red-600 dark:text-red-400" />
        <h3 className="font-semibold text-t1">{t('analytics:cancellations.dineInTitle')}</h3>
      </div>
      <p className="text-xs text-t3 mb-3">{t('analytics:cancellations.dineInHint')}</p>
      <p className="text-sm text-t2 mb-3" data-testid="dinein-cancellations-summary">
        {[
          dineIn.items.count > 0 ? t('analytics:cancellations.dineInItems', { count: dineIn.items.count, amount: formatEuro(dineIn.items.amount) }) : null,
          dineIn.tables.count > 0 ? t('analytics:cancellations.dineInTables', { count: dineIn.tables.count, amount: formatEuro(dineIn.tables.amount) }) : null,
        ].filter(Boolean).join(' · ')}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="dinein-cancellations-table">
          <thead>
            <tr className="border-b border-line text-xs text-t3">
              <th className="text-left py-2 pr-3 font-medium">{t('analytics:cancellations.colDate')}</th>
              <th className="text-left py-2 pr-3 font-medium">{t('analytics:cancellations.colTable')}</th>
              <th className="text-left py-2 pr-3 font-medium">{t('analytics:cancellations.colItem')}</th>
              <th className="text-right py-2 pr-3 font-medium">{t('analytics:cancellations.colQty')}</th>
              <th className="text-right py-2 pr-3 font-medium">{t('analytics:cancellations.colAmount')}</th>
              <th className="text-left py-2 pr-3 font-medium">{t('analytics:cancellations.colReason')}</th>
              <th className="text-left py-2 font-medium">{t('analytics:cancellations.colWho')}</th>
            </tr>
          </thead>
          <tbody>
            {dineIn.rows.map(r => (
              <tr key={r.id} className="border-b border-line/50 last:border-b-0">
                <td className="py-2 pr-3 text-t2 whitespace-nowrap tabular-nums">{formatWhen(r.at)}</td>
                <td className="py-2 pr-3 text-t2 whitespace-nowrap">{r.table ?? '—'}</td>
                <td className="py-2 pr-3 text-t1">{r.kind === 'table' ? t('analytics:cancellations.wholeTable') : r.item}</td>
                <td className="py-2 pr-3 text-right text-t2 tabular-nums">{r.quantity != null ? formatQty(r.quantity) : '—'}</td>
                <td className="py-2 pr-3 text-right text-t1 font-medium whitespace-nowrap tabular-nums">{formatEuro(r.amount)}</td>
                <td className="py-2 pr-3 text-t2">{r.reason || '—'}</td>
                <td className="py-2 text-t2 whitespace-nowrap">{r.who}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dineIn.truncated && (
        <p className="text-[11px] text-t4 mt-2">{t('analytics:cancellations.dineInTruncated', { shown: dineIn.rows.length, total: dineInTotal })}</p>
      )}
    </div>
  ) : null;
  const dineInFailed = dineIn === null
    ? <p className="text-xs text-t3" data-testid="dinein-cancellations-failed">{t('analytics:cancellations.dineInFailed')}</p>
    : null;

  if (noOrders) {
    return (
      <div className="space-y-4">
        <div className="bg-surface-1/50 border border-line rounded-2xl p-6 text-t3 text-sm">
          <div className="flex items-center gap-2 mb-2 text-t1">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <h3 className="font-semibold">{t('analytics:cancellations.title', 'Cancellations')}</h3>
          </div>
          {/* Χωρίς τίποτα πουθενά → «καμία ακύρωση»· με ακυρώσεις στα τραπέζια → μόνο ότι δεν υπάρχουν παραγγελίες. */}
          {dineIn && dineInTotal === 0
            ? t('analytics:cancellations.noneAll')
            : t('analytics:cancellations.none', 'No cancelled orders in this period.')}
        </div>
        {dineInBlock}
        {dineInFailed}
      </div>
    );
  }

  const maxReasonCount = Math.max(...data.by_reason.map(r => r.count), 1);
  const maxEmpCount    = Math.max(...data.by_employee.map(r => r.count), 1);

  return (
    <div className="space-y-4">
      <div className="bg-surface-1/50 border border-line rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <h3 className="font-semibold text-t1">{t('analytics:cancellations.title', 'Cancellations')}</h3>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <p className="text-xs text-t3 mb-1">{t('analytics:cancellations.totalLabel', 'Total cancelled')}</p>
            <div className="text-2xl font-bold text-t1">{data.summary.total_cancelled}</div>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
            <p className="text-xs text-t3 mb-1 flex items-center gap-1">
              <TrendingDown className="w-3 h-3" /> {t('analytics:cancellations.rateLabel', 'Cancellation rate')}
            </p>
            <div className="text-2xl font-bold text-t1">{data.summary.cancellation_rate}%</div>
            <p className="text-[11px] text-t4 mt-0.5">{data.summary.total_orders} {t('analytics:cancellations.totalOrders', 'total orders')}</p>
          </div>
          <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 col-span-2 lg:col-span-2">
            <p className="text-xs text-t3 mb-1">{t('analytics:cancellations.revenueLostLabel', 'Lost revenue')}</p>
            <div className="text-2xl font-bold text-rose-700 dark:text-rose-300">{formatEuro(data.summary.revenue_lost)}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* By reason */}
          <div className="bg-surface-0/40 rounded-xl p-4 border border-line">
            <div className="flex items-center gap-2 mb-3 text-t2">
              <MessageSquare className="w-4 h-4" />
              <h4 className="text-sm font-semibold">{t('analytics:cancellations.byReason', 'By reason')}</h4>
            </div>
            <div className="space-y-2">
              {data.by_reason.slice(0, 8).map((r, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-t2 truncate pr-2">{r.reason}</span>
                    <span className="text-t3 flex-shrink-0">{r.count} · {formatEuro(r.revenue_lost)}</span>
                  </div>
                  <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500 rounded-full" style={{ width: `${(r.count / maxReasonCount) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By employee */}
          <div className="bg-surface-0/40 rounded-xl p-4 border border-line">
            <div className="flex items-center gap-2 mb-3 text-t2">
              <Users className="w-4 h-4" />
              <h4 className="text-sm font-semibold">{t('analytics:cancellations.byEmployee', 'By employee')}</h4>
            </div>
            <div className="space-y-2">
              {data.by_employee.slice(0, 8).map((r, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-t2 truncate pr-2">{r.name}</span>
                    <span className="text-t3 flex-shrink-0">{r.count} · {formatEuro(r.revenue_lost)}</span>
                  </div>
                  <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(r.count / maxEmpCount) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {dineInBlock}
      {dineInFailed}
    </div>
  );
};

export default CancellationsAnalytics;
